import * as XLSX from "xlsx-js-style";
import type { AttributeField } from "@/features/properties/types/propertyAttributes";
import type { DeficiencyPayload, PropertyRow } from "./types";

export function buildStyledXlsx(rows: string[][], sheetName: string, widths: number[]): ArrayBuffer {
  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  worksheet["!cols"] = widths.map((wch) => ({ wch }));

  for (let r = 0; r < rows.length; r++) {
    for (let c = 0; c < rows[r].length; c++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (!worksheet[ref]) continue;

      worksheet[ref].s = {
        alignment: { wrapText: true, vertical: "top" },
      };

      if (r === 0) {
        worksheet[ref].s.font = { color: { rgb: "FFFFFF" }, bold: true };
        worksheet[ref].s.fill = { fgColor: { rgb: "000000" } };
      }
    }
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  return XLSX.write(workbook, { type: "array", bookType: "xlsx" }) as ArrayBuffer;
}

export function walkSurveyField(
  field: AttributeField,
  parent: string,
  onLeaf: (field: Exclude<AttributeField, { type: "node" }>, category: string) => void,
): void {
  if (field.type === "node") {
    const nextParent = parent ? `${parent} > ${field.title}` : field.title;
    field.fields.forEach((child) => walkSurveyField(child, nextParent, onLeaf));
    return;
  }

  onLeaf(field, parent.split(" > ")[0] || "General");
}

export function answerForField(field: Exclude<AttributeField, { type: "node" }>): string {
  switch (field.type) {
    case "photos":
      return field.value && field.value.length > 0 ? `${field.value.length} photo(s) attached` : "";
    case "area":
      return field.value ? field.value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() : "";
    case "multiSelect":
      return field.value && field.value.length > 0 ? field.value.join("; ") : "";
    default:
      return field.value == null ? "" : String(field.value);
  }
}

export function evidenceForField(field: Exclude<AttributeField, { type: "node" }>): string[] {
  const urls: string[] = [];
  if (Array.isArray(field.evidence)) {
    urls.push(...field.evidence.filter((url) => typeof url === "string" && url.trim() !== ""));
  }
  if (field.type === "photos" && Array.isArray(field.value)) {
    urls.push(...field.value.filter((url) => typeof url === "string" && url.trim() !== ""));
  }
  return Array.from(new Set(urls));
}

export function parseFields(value: unknown): AttributeField[] {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    return Array.isArray(parsed) ? parsed as AttributeField[] : [];
  } catch {
    return [];
  }
}

export function parseDeficiencyPayload(value: unknown): DeficiencyPayload {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed as DeficiencyPayload
      : {};
  } catch {
    return {};
  }
}

export function propertyLabel(property: PropertyRow | undefined): string {
  if (!property) return "";
  return property.store_number || property.name || property.address || property.id;
}

export function relationName(value: { name: string } | { name: string }[] | null): string {
  if (Array.isArray(value)) return value[0]?.name ?? "";
  return value?.name ?? "";
}

export function flattenAttributes(attrs: Record<string, unknown> | null): string {
  if (!attrs || typeof attrs !== "object") return "";
  return Object.entries(attrs)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => `${key.replace(/_/g, " ")}: ${String(value)}`)
    .join("; ");
}

export function cleanTitle(title: string): string {
  return title.replace(/\s*\[.*?\]\s*$/, "");
}

export async function runWithConcurrency(tasks: Array<() => Promise<void>>, limit: number): Promise<void> {
  let cursor = 0;
  const workerCount = Math.min(limit, tasks.length);

  const workers = Array.from({ length: workerCount }, async () => {
    while (cursor < tasks.length) {
      const task = tasks[cursor];
      cursor += 1;
      await task();
    }
  });

  await Promise.all(workers);
}
